import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { API_MAX_LIMIT } from '../models/api.models';
import { Keywords } from '../models/common.models';
import {
  CLICKS,
  CLICK_THROUGH_RATE,
  CLICK_THROUGH_RATE_CLICKS,
  CONVERSIONS,
  CONVERSIONS_CLICKS,
  CONVERSION_RATE,
  CONVERSION_RATE_CLICKS,
  COST,
  COST_FOR_CLICK,
  COST_FOR_PROFIT,
  COST_FOR_REVENUE,
  COST_FOR_VCPM,
  COST_PER_CLICK,
  COST_PER_CONVERSION,
  COST_PER_CONVERSION_CLICKS,
  COST_PER_MILLE,
  COST_REVENUE_RATIO,
  COST_REVENUE_RATIO_CLICKS,
  IMPRESSIONS,
  IMPRESSIONS_TOTAL,
  PRICE,
  PROFIT_GROSS,
  PROFIT_NET,
  RETURN_ON_INVESTMENT,
  REVENUE,
  REVENUE_CLICKS,
  ROAS,
  ROAS_CLICKS,
  VIEW_IMPRESSIONS,
} from '../models/metrics.constants';
import { LoggingService } from '../services/error-logging.service';
import {
  averageByMetric,
  convertMicrostoNumbers,
  getACoS,
  getClickThroughRate,
  getConversionRate,
  getCostPerConversion,
  getEffectiveCPC,
  getEffectiveCPM,
  getProductsReturnOnInvestment,
  getReturnOnAdSpend,
  summationByMetric,
} from '../services/metrics-calculations.service';
import { UtilitiesService, encode32 } from '../services/utilities.service';

@Injectable()
export class ProductsAPI {
  pageSize = 25;
  constructor(
    private http: HttpClient,
    private logger: LoggingService,
    private utilitiesService: UtilitiesService,
  ) {}

  public getProductByID(id: string, adspertID: string, refiners: any[]) {
    refiners.push(['filter_adspert_product_id', encodeURIComponent(id)], ['group_by', 'adspert_product_id']);
    return this.http
      .get(
        `${
          environment.app.resources.dashboard
        }preview/accounts/${adspertID}/performance/products?${this.utilitiesService.refinersToUrl(refiners)}`,
        {},
      )
      .pipe(
        map((response: any) => {
          return convertMicrostoNumbers(response.data).map((item: any) => ({
            ...item,
            id: item.adspert_product_id,
            title: item.title,
            image: item.image_url,
            asin: item.amz_asin,
            sku: item.amz_sku,
            adwordsId: item.ggl_offer_id,
          }))[0];
        }),
        catchError((error: any) => {
          this.logger.logError('Failed to fetch products', error, 'error');
          return throwError('Failed to fetch products');
        }),
      );
  }
  public getKeywords(
    adspertID: string,
    productID: string,
    refiners: any[],
  ): Observable<{ items: Keywords[]; total: number }> {
    refiners.push(['sort_share_of_conversions', -1], ['limit', 10]);
    return this.http
      .get(
        // eslint-disable-next-line max-len
        `${environment.app.resources.dashboard}preview/accounts/${adspertID}/products/${encode32(
          productID,
        )}/converting-criteria?${this.utilitiesService.refinersToUrl(refiners)}`,
        {},
      )
      .pipe(
        map((response: any) => {
          return {
            total: response.meta.pagination.total,
            items: response.data.map((item: any) => {
              return {
                name: item.criterion_name,
                criterionType: item.criterion_type,
                matchType: item.match_type ? item.match_type.toLowerCase() : '',
                conversionShare: item.share_of_conversions,
              } as Keywords;
            }),
          };
        }),
        catchError((error: any) => {
          this.logger.logError('Failed to fetch keywords', error, 'error');
          return throwError('Failed to fetch keywords');
        }),
      );
  }

  public mapComputedValues(value: any) {
    //@todo
    value = value.split('sort_');
    // return 'sort_' + value[1];

    let returnValue = '';
    switch (value[1]) {
      case CLICK_THROUGH_RATE:
        returnValue = 'click_through_rate';
        break;
      case CONVERSION_RATE:
        returnValue = 'conversion_rate';
        break;
      case COST_PER_CONVERSION:
        returnValue = 'cost_per_conversion';
        break;
      case COST_PER_CLICK:
        returnValue = 'cost_per_click';
        break;
      case COST_REVENUE_RATIO:
        returnValue = 'cost_revenue_ratio';
        break;
      case RETURN_ON_INVESTMENT:
        returnValue = 'return_on_investment';
        break;
      default:
        returnValue = value[1];
        break;
    }
    return 'sort_' + returnValue;
  }

  public getProductPerformanceByCampaigns(
    productID: string,
    adspertID: string,
    refiners: any[],
    groupByCampaigns: boolean = true,
    paging = 0,
  ) {
    refiners.push(
      ['group_by', groupByCampaigns ? 'campaign_id' : 'date'],
      ['limit', groupByCampaigns ? this.pageSize : API_MAX_LIMIT],
      ['offset', !paging ? 0 : paging * this.pageSize],
    );

    if (!groupByCampaigns) {
      refiners.push(['sort_date', 1]);
    } else {
      refiners.forEach((item) => {
        if (item[0].startsWith('sort_')) {
          item[0] = this.mapComputedValues(item[0]);
        }
      });
    }
    const url = `${environment.app.resources.dashboard}preview/accounts/${adspertID}/performance/products/${encode32(
      productID,
    )}/campaigns?${this.utilitiesService.refinersToUrl(refiners)}`;
    return this.http.get(url, {}).pipe(
      map((response: any) =>
        groupByCampaigns ? this._transformProductCampaignsHistory(response) : this._transformSummaryData(response.data),
      ),
      catchError((error: any) => {
        this.logger.logError('Failed to fetch Product Performance By Campaigns', error, 'error');
        return throwError('Failed to fetch Product Performance By Campaigns');
      }),
    );
  }

  private _transformProductCampaignsHistory(response) {
    return {
      total: response.meta.pagination.total,
      items: convertMicrostoNumbers(response.data).map((item: any) => {
        return {
          ...item,
          id: item.campaign_id,
          name: item.campaign_name,
          status: item.amz_effective_status && item.amz_effective_status.replace(/-/g, '_'),
          targetType: item.amz_targeting_type,
          type: item.campaign_type,
        };
      }),
    };
  }

  public getProductPerformanceByAdGroups(
    productID: string,
    adspertID: string,
    refiners: any[],
    groupByAdGroups: boolean = true,
  ) {
    refiners.push(
      ['group_by', groupByAdGroups ? 'adgroup_id' : 'date'],
      ['limit', groupByAdGroups ? this.pageSize : API_MAX_LIMIT],
      [
        'include',
        groupByAdGroups
          ? 'campaign_id,adgroup_id,adgroup_name,amz_effective_status,amz_adgroup_type,impressions,clicks,conversions,cost,revenue,cost_per_click,cost_per_conversion,conversion_rate,click_through_rate,return_on_investment,cost_revenue_ratio,price'
          : 'date,impressions,clicks,conversions,cost,revenue,cost_per_click,cost_per_conversion,conversion_rate,click_through_rate,return_on_investment,cost_revenue_ratio,price',
      ],
    );
    if (!groupByAdGroups) {
      refiners.push(['sort_date', 1]);
    } else {
      refiners.forEach((item) => {
        if (item[0].startsWith('sort_')) {
          item[0] = this.mapComputedValues(item[0]);
        }
      });
    }
    const url = `${environment.app.resources.dashboard}preview/accounts/${adspertID}/performance/products/${encode32(
      productID,
    )}/adgroups?${this.utilitiesService.refinersToUrl(refiners)}`;
    return this.http.get(url, {}).pipe(
      map((response: any) =>
        groupByAdGroups ? this._transformProductAdGroupsHistory(response) : this._transformSummaryData(response.data),
      ),
      catchError((error: any) => {
        this.logger.logError('Failed to fetch Product Performance By Campaigns', error, 'error');
        return throwError('Failed to fetch Product Performance By Campaigns');
      }),
    );
  }

  private _transformProductAdGroupsHistory(response) {
    return {
      total: response.meta.pagination.total,
      items: convertMicrostoNumbers(response.data).map((item: any) => {
        return {
          ...item,
          id: item.adgroup_id,
          campaignId: item.campaign_id,
          name: item.adgroup_name,
          status: item.amz_effective_status && item.amz_effective_status.replace(/-/g, '_'),
          targetType: item.amz_adgroup_type,
          type: item.campaign_type,
        };
      }),
    };
  }

  public getProducts(adspertID: string, refiners: any[], groupByProducts: boolean = true, paging = 0) {
    refiners.push(
      ['group_by', groupByProducts ? 'adspert_product_id' : 'date'],
      ['limit', groupByProducts ? this.pageSize : API_MAX_LIMIT],
      ['offset', !paging ? 0 : paging * this.pageSize],
    );
    if (!groupByProducts) {
      refiners.push(['sort_date', 1]);
      refiners.push([
        'exclude',
        'adspert_product_id,amz_effective_status,title,image_url,amz_asin,amz_sku,ggl_offer_id',
      ]);
    }
    const url = `${
      environment.app.resources.dashboard
    }preview/accounts/${adspertID}/performance/products?${this.utilitiesService.refinersToUrl(refiners)}`;
    return this.http.get(url, {}).pipe(
      map(
        (response: any) =>
          groupByProducts
            ? this._transformProductsByHistory(response) // list of products
            : this._transformSummaryData(response.data), // summation of metrics by date
      ),
      catchError((error: any) => {
        this.logger.logError('Failed to fetch products', error, 'error');
        return throwError('Failed to fetch products');
      }),
    );
  }

  private _transformProductsByHistory(response) {
    return {
      total: response.meta.pagination.total,
      items: convertMicrostoNumbers(response.data).map((item: any) => {
        return {
          ...item,
          id: item.adspert_product_id,
          title: item.title,
          image: item.image_url,
          status: item.amz_effective_status?.replace(/-/g, '_'),
          asin: item.amz_asin,
          sku: item.amz_sku,
        };
      }),
    };
  }

  private _transformSummaryData(data) {
    const summary: any = {
      [COST]: summationByMetric(data, COST),
      [COST_FOR_REVENUE]: summationByMetric(data, COST_FOR_REVENUE),
      [COST_FOR_CLICK]: summationByMetric(data, COST_FOR_CLICK),
      [COST_FOR_PROFIT]: summationByMetric(data, COST_FOR_PROFIT),
      [COST_FOR_VCPM]: summationByMetric(data, COST_FOR_VCPM),
      [IMPRESSIONS]: summationByMetric(data, IMPRESSIONS),
      [IMPRESSIONS_TOTAL]: summationByMetric(data, IMPRESSIONS_TOTAL),
      [CLICKS]: summationByMetric(data, CLICKS),
      [CONVERSIONS]: summationByMetric(data, CONVERSIONS),
      [REVENUE]: summationByMetric(data, REVENUE),
      [PRICE]: averageByMetric(data, PRICE),
      [PROFIT_GROSS]: summationByMetric(data, PROFIT_GROSS),
      [PROFIT_NET]: summationByMetric(data, PROFIT_NET),

      [VIEW_IMPRESSIONS]: summationByMetric(data, VIEW_IMPRESSIONS),
      [CONVERSIONS_CLICKS]: summationByMetric(data, CONVERSIONS_CLICKS),
      [REVENUE_CLICKS]: summationByMetric(data, REVENUE_CLICKS),
    };
    const COST_WITHOUT_CLICKS = summary[COST] - summary[COST_FOR_CLICK];

    summary[CLICK_THROUGH_RATE] = getClickThroughRate(summary[CLICKS], summary[IMPRESSIONS_TOTAL]);
    summary[CLICK_THROUGH_RATE_CLICKS] = getClickThroughRate(summary[CLICKS], summary[IMPRESSIONS]);

    summary[CONVERSION_RATE] = getConversionRate(summary[CONVERSIONS], summary[CLICKS]);
    summary[CONVERSION_RATE_CLICKS] = getConversionRate(summary[CONVERSIONS_CLICKS], summary[CLICKS]);

    summary[COST_PER_CONVERSION] = getCostPerConversion(COST_WITHOUT_CLICKS, summary[CONVERSIONS]);
    summary[COST_PER_CONVERSION_CLICKS] = getCostPerConversion(COST_WITHOUT_CLICKS, summary[CONVERSIONS_CLICKS]);

    summary[COST_REVENUE_RATIO] = getACoS(summary[COST_FOR_REVENUE], summary[REVENUE]);
    summary[COST_REVENUE_RATIO_CLICKS] = getACoS(summary[COST_FOR_REVENUE], summary[REVENUE_CLICKS]);

    summary[COST_PER_CLICK] = getEffectiveCPC(summary[COST], summary[CLICKS]);

    summary[RETURN_ON_INVESTMENT] = getProductsReturnOnInvestment(summary[REVENUE], summary[COST_FOR_PROFIT]);

    summary[ROAS] = getReturnOnAdSpend(summary[REVENUE], summary[COST_FOR_REVENUE]);
    summary[ROAS_CLICKS] = getReturnOnAdSpend(summary[REVENUE_CLICKS], summary[COST_FOR_REVENUE]);

    summary[COST_PER_CLICK] = getEffectiveCPC(summary[COST], summary[CLICKS]);
    summary[COST_PER_MILLE] = getEffectiveCPM(summary[COST_FOR_VCPM], summary[VIEW_IMPRESSIONS]);

    summary.hasVcpm = !!data.find((d) => !!d.has_vcpm);
    if (!summary.hasVcpm && summary[VIEW_IMPRESSIONS] === 0) summary[VIEW_IMPRESSIONS] = null;

    return { points: convertMicrostoNumbers(data), summary };
  }
}
